import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import DownloadIcon from "@mui/icons-material/Download";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import {
  decideDoctor,
  downloadDoctorDocumentAdmin,
  listApprovedDoctors,
  listDoctorDocumentsAdmin,
  listPendingDoctors,
  listSuspendedDoctors,
  reactivateDoctor,
  suspendDoctor,
} from "@/api/userApi";
import { PageHeader } from "@/components/common/PageHeader";
import type { DoctorDetail, DoctorDocumentDto } from "@/types/domain";

export default function AdminDoctorsPage() {
  const [tab, setTab] = useState(0);

  return (
    <Box>
      <PageHeader
        title="Doctor management"
        subtitle="Review pending verifications, view approved doctors, and manage suspensions."
      />
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label="Pending review" />
          <Tab label="Approved" />
          <Tab label="Suspended" />
        </Tabs>
      </Box>
      {tab === 0 && <PendingDoctorsTab />}
      {tab === 1 && <ApprovedDoctorsTab />}
      {tab === 2 && <SuspendedDoctorsTab />}
    </Box>
  );
}

// ── Pending Tab ───────────────────────────────────────────────────────

function PendingDoctorsTab() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin-pending-doctors"],
    queryFn: listPendingDoctors,
  });

  const [rejecting, setRejecting] = useState<DoctorDetail | null>(null);
  const [rejectReason, setRejectReason] = useState("");

  const decide = useMutation({
    mutationFn: (args: {
      doctorId: string;
      approve: boolean;
      reason?: string;
    }) =>
      decideDoctor(args.doctorId, {
        approve: args.approve,
        reason: args.reason,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-pending-doctors"] });
      qc.invalidateQueries({ queryKey: ["admin-approved-doctors"] });
      setRejecting(null);
      setRejectReason("");
    },
  });

  return (
    <>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load pending doctors.
        </Alert>
      )}
      <Paper variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Registration</TableCell>
              <TableCell>Council</TableCell>
              <TableCell>Documents</TableCell>
              <TableCell>Submitted</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={6}>Loading…</TableCell>
              </TableRow>
            )}
            {!isLoading && (data ?? []).length === 0 && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography color="text.secondary" py={2}>
                    No doctors are currently awaiting review.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {(data ?? []).map((d) => (
              <TableRow key={d.id}>
                <TableCell>
                  <Stack>
                    <Typography fontWeight={700}>{d.fullName}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {d.qualification}
                    </Typography>
                  </Stack>
                </TableCell>
                <TableCell>{d.registrationNumber ?? "—"}</TableCell>
                <TableCell>
                  <Stack>
                    <span>{d.registrationCouncil ?? "—"}</span>
                    {d.registrationYear && (
                      <Typography variant="caption" color="text.secondary">
                        Registered {d.registrationYear}
                      </Typography>
                    )}
                  </Stack>
                </TableCell>
                <TableCell>
                  <Stack spacing={0.5}>
                    {d.licenseDocUrl && (
                      <Link
                        href={d.licenseDocUrl}
                        target="_blank"
                        rel="noreferrer"
                      >
                        License
                      </Link>
                    )}
                    {d.idProofDocUrl && (
                      <Link
                        href={d.idProofDocUrl}
                        target="_blank"
                        rel="noreferrer"
                      >
                        ID proof
                      </Link>
                    )}
                    {d.degreeDocUrl && (
                      <Link
                        href={d.degreeDocUrl}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Degree
                      </Link>
                    )}
                    {!d.licenseDocUrl &&
                      !d.idProofDocUrl &&
                      !d.degreeDocUrl && <Chip size="small" label="None" />}
                  </Stack>
                </TableCell>
                <TableCell>
                  {d.submittedAt
                    ? new Date(d.submittedAt).toLocaleString()
                    : "—"}
                </TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button
                      size="small"
                      color="error"
                      variant="outlined"
                      onClick={() => setRejecting(d)}
                    >
                      Reject
                    </Button>
                    <Button
                      size="small"
                      color="success"
                      variant="contained"
                      disabled={decide.isPending}
                      onClick={() =>
                        decide.mutate({ doctorId: d.id, approve: true })
                      }
                    >
                      Approve
                    </Button>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Dialog
        open={Boolean(rejecting)}
        onClose={() => setRejecting(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Reject {rejecting?.fullName}</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            This reason is shown back to the doctor so they can fix and
            resubmit.
          </Typography>
          <TextField
            label="Reason"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            fullWidth
            multiline
            rows={3}
            autoFocus
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejecting(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={!rejectReason.trim() || decide.isPending}
            onClick={() =>
              rejecting &&
              decide.mutate({
                doctorId: rejecting.id,
                approve: false,
                reason: rejectReason.trim(),
              })
            }
          >
            {decide.isPending ? "Rejecting…" : "Reject"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

// ── Approved Tab ──────────────────────────────────────────────────────

function ApprovedDoctorsTab() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin-approved-doctors"],
    queryFn: listApprovedDoctors,
  });

  const [expanded, setExpanded] = useState<string | null>(null);
  const [suspending, setSuspending] = useState<DoctorDetail | null>(null);
  const [suspendReason, setSuspendReason] = useState("");

  const suspend = useMutation({
    mutationFn: (args: { doctorId: string; reason?: string }) =>
      suspendDoctor(args.doctorId, { reason: args.reason }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-approved-doctors"] });
      qc.invalidateQueries({ queryKey: ["admin-suspended-doctors"] });
      setSuspending(null);
      setSuspendReason("");
    },
  });

  const toggleExpand = useCallback(
    (id: string) => setExpanded((prev) => (prev === id ? null : id)),
    [],
  );

  return (
    <>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load approved doctors.
        </Alert>
      )}
      <Paper variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>Name</TableCell>
              <TableCell>Qualification</TableCell>
              <TableCell>Specializations</TableCell>
              <TableCell>Fee (INR)</TableCell>
              <TableCell>Rating</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={7}>Loading…</TableCell>
              </TableRow>
            )}
            {!isLoading && (data ?? []).length === 0 && (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography color="text.secondary" py={2}>
                    No approved doctors yet.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {(data ?? []).map((d) => (
              <DoctorExpandableRow
                key={d.id}
                doctor={d}
                expanded={expanded === d.id}
                onToggle={() => toggleExpand(d.id)}
                onSuspend={() => setSuspending(d)}
              />
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Dialog
        open={Boolean(suspending)}
        onClose={() => setSuspending(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Suspend Dr. {suspending?.fullName}</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Suspending this doctor will:
            <ul style={{ margin: "4px 0 0 0", paddingLeft: 20 }}>
              <li>Hide them from patient search immediately</li>
              <li>
                Cancel all their scheduled appointments (patients get full
                refund eligibility)
              </li>
              <li>Show a suspension notice on the doctor's dashboard</li>
            </ul>
          </Alert>
          <TextField
            label="Reason (optional, shown to doctor)"
            value={suspendReason}
            onChange={(e) => setSuspendReason(e.target.value)}
            fullWidth
            multiline
            rows={3}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSuspending(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            startIcon={<BlockIcon />}
            disabled={suspend.isPending}
            onClick={() =>
              suspending &&
              suspend.mutate({
                doctorId: suspending.id,
                reason: suspendReason.trim() || undefined,
              })
            }
          >
            {suspend.isPending ? "Suspending…" : "Suspend doctor"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

function DoctorExpandableRow({
  doctor: d,
  expanded,
  onToggle,
  onSuspend,
}: {
  doctor: DoctorDetail;
  expanded: boolean;
  onToggle: () => void;
  onSuspend: () => void;
}) {
  return (
    <>
      <TableRow sx={{ "& > *": { borderBottom: expanded ? "unset" : undefined } }}>
        <TableCell padding="checkbox">
          <IconButton size="small" onClick={onToggle}>
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Typography fontWeight={700}>{d.fullName}</Typography>
        </TableCell>
        <TableCell>{d.qualification ?? "—"}</TableCell>
        <TableCell>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {(Array.isArray(d.specializations)
              ? d.specializations
              : d.specialization
                ? [d.specialization]
                : []
            ).map((s) =>
              typeof s === "string" ? (
                <Chip key={s} label={s} size="small" />
              ) : null,
            )}
          </Stack>
        </TableCell>
        <TableCell>
          {d.consultationFee != null ? `₹${d.consultationFee}` : "—"}
        </TableCell>
        <TableCell>
          {d.averageRating != null
            ? `${d.averageRating} (${d.reviewCount ?? 0})`
            : "—"}
        </TableCell>
        <TableCell align="right">
          <Tooltip title="Suspend this doctor">
            <Button
              size="small"
              color="error"
              variant="outlined"
              startIcon={<BlockIcon />}
              onClick={onSuspend}
            >
              Suspend
            </Button>
          </Tooltip>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={7} sx={{ py: 0 }}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <DoctorDetailPanel doctor={d} />
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

function DoctorDetailPanel({ doctor: d }: { doctor: DoctorDetail }) {
  const { data: docs, isLoading } = useQuery({
    queryKey: ["admin-doctor-docs", d.id],
    queryFn: () => listDoctorDocumentsAdmin(d.id),
  });

  const handleDownload = async (doc: DoctorDocumentDto) => {
    const blob = await downloadDoctorDocumentAdmin(doc.id);
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = doc.fileName;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ py: 2, px: 1 }}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="subtitle2" color="text.secondary">
            Bio
          </Typography>
          <Typography variant="body2">{d.bio || "Not provided"}</Typography>
        </Box>

        <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              Experience
            </Typography>
            <Typography variant="body2">
              {d.experienceYears != null
                ? `${d.experienceYears} years`
                : "Not provided"}
            </Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              Registration number
            </Typography>
            <Typography variant="body2">
              {d.registrationNumber ?? "Not provided"}
            </Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              Council
            </Typography>
            <Typography variant="body2">
              {d.registrationCouncil ?? "Not provided"}
              {d.registrationYear ? ` (${d.registrationYear})` : ""}
            </Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              Approved
            </Typography>
            <Typography variant="body2">
              {d.reviewedAt
                ? new Date(d.reviewedAt).toLocaleDateString()
                : "—"}
            </Typography>
          </Box>
        </Stack>

        {/* Legacy URL-based documents */}
        {(d.licenseDocUrl || d.idProofDocUrl || d.degreeDocUrl) && (
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Document links
            </Typography>
            <Stack direction="row" spacing={2}>
              {d.licenseDocUrl && (
                <Link href={d.licenseDocUrl} target="_blank" rel="noreferrer">
                  License
                </Link>
              )}
              {d.idProofDocUrl && (
                <Link href={d.idProofDocUrl} target="_blank" rel="noreferrer">
                  ID Proof
                </Link>
              )}
              {d.degreeDocUrl && (
                <Link href={d.degreeDocUrl} target="_blank" rel="noreferrer">
                  Degree
                </Link>
              )}
            </Stack>
          </Box>
        )}

        {/* BLOB-uploaded documents */}
        <Box>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Uploaded documents
          </Typography>
          {isLoading && (
            <Typography variant="body2" color="text.secondary">
              Loading…
            </Typography>
          )}
          {!isLoading && (docs ?? []).length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No documents uploaded.
            </Typography>
          )}
          {(docs ?? []).length > 0 && (
            <Stack spacing={0.5}>
              {(docs ?? []).map((doc) => (
                <Stack
                  key={doc.id}
                  direction="row"
                  alignItems="center"
                  spacing={1}
                >
                  <Chip
                    size="small"
                    label={doc.documentType}
                    variant="outlined"
                  />
                  <Typography variant="body2">{doc.fileName}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    ({(doc.fileSize / 1024).toFixed(0)} KB)
                  </Typography>
                  {doc.notes && (
                    <Typography variant="caption" color="text.secondary">
                      — {doc.notes}
                    </Typography>
                  )}
                  <IconButton size="small" onClick={() => handleDownload(doc)}>
                    <DownloadIcon fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
            </Stack>
          )}
        </Box>
      </Stack>
    </Box>
  );
}

// ── Suspended Tab ─────────────────────────────────────────────────────

function SuspendedDoctorsTab() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin-suspended-doctors"],
    queryFn: listSuspendedDoctors,
  });

  const enable = useMutation({
    mutationFn: (doctorId: string) => reactivateDoctor(doctorId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-suspended-doctors"] });
      qc.invalidateQueries({ queryKey: ["admin-approved-doctors"] });
    },
  });

  return (
    <>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load suspended doctors.
        </Alert>
      )}
      <Paper variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Qualification</TableCell>
              <TableCell>Suspension reason</TableCell>
              <TableCell>Suspended at</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={5}>Loading…</TableCell>
              </TableRow>
            )}
            {!isLoading && (data ?? []).length === 0 && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography color="text.secondary" py={2}>
                    No suspended doctors.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {(data ?? []).map((d) => (
              <TableRow key={d.id}>
                <TableCell>
                  <Typography fontWeight={700}>{d.fullName}</Typography>
                </TableCell>
                <TableCell>{d.qualification ?? "—"}</TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {d.suspensionReason || "No reason provided"}
                  </Typography>
                </TableCell>
                <TableCell>
                  {d.suspendedAt
                    ? new Date(d.suspendedAt).toLocaleString()
                    : "—"}
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Reactivate this doctor — they will reappear in patient search">
                    <Button
                      size="small"
                      color="success"
                      variant="contained"
                      startIcon={<CheckCircleOutlineIcon />}
                      disabled={enable.isPending}
                      onClick={() => enable.mutate(d.id)}
                    >
                      {enable.isPending ? "Enabling…" : "Enable"}
                    </Button>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </>
  );
}
